/**
 * A keystroke as an editing intent, or nothing.
 *
 * Kept apart from the board that uses it so the mapping can be read in one screen: letters
 * write notes, digits pick durations, and the rest is the handful of things a hand on the
 * keyboard expects. Nothing here touches a score -- it answers with an `EntryInput` and the
 * editor decides what that means where the caret is.
 *
 * Note letters follow MuseScore and Sibelius rather than a piano keyboard, because this is
 * the layout anyone who has typed music before already has in their fingers.
 *
 * A tab staff reads two of those keys differently, because tablature is typed as numbers on
 * a string rather than letters on a stave: there the digits are frets and the plain up and
 * down arrows move between strings. Which reading applies is not something a keystroke can
 * know, so the caller says it.
 */

import type { EntryInput, EntryState } from "@app/score-editor";
import type { NoteType } from "@app/score-editor";

/** The keystroke as it reaches us, without a React event attached. */
export interface Keystroke {
  readonly key: string;
  /** The physical key, which a modifier cannot change into another character. */
  readonly code: string;
  readonly shiftKey: boolean;
  readonly altKey: boolean;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
}

const LETTERS = new Set(["a", "b", "c", "d", "e", "f", "g"]);

/** Digit 1 is the longest note this board offers, and each one after it halves. */
const DURATIONS: Readonly<Record<string, NoteType>> = {
  "1": "whole",
  "2": "half",
  "3": "quarter",
  "4": "eighth",
  "5": "16th",
  "6": "32nd",
};

/** The same values in order, for the two keys that halve and double the current one. */
const LADDER: readonly NoteType[] = Object.values(DURATIONS);

/**
 * A shorter or longer note value, stopping at the ends of the board's ladder.
 *
 * Q and W are here because a tab staff spends its digits on frets: they are how the value
 * is changed without them, and they follow MuseScore, where the same two keys halve and
 * double the note. They work on every staff, not only a fretted one.
 */
function shifted(state: EntryState, by: number): EntryInput {
  const at = LADDER.indexOf(state.duration.type);
  const next = LADDER[Math.min(Math.max((at < 0 ? 2 : at) + by, 0), LADDER.length - 1)];
  return { kind: "duration", type: next ?? state.duration.type };
}

/**
 * The digit a stroke asks for, by the character where there is one and by the key itself
 * where there is not.
 *
 * Alt is what makes the second reading necessary: held down it does not always leave the
 * character alone, and a layout that answers alt-1 with a punctuation mark would take the
 * frets above nine away from anyone typing on it. The physical key says `Digit1` either
 * way. Only tablature reads a stroke this way -- a note value is still the character typed,
 * so a layout that puts a digit behind shift is not made to fight its own keyboard.
 */
function digitOf(stroke: Keystroke): number | null {
  if (/^[0-9]$/.test(stroke.key)) return Number(stroke.key);
  const physical = /^Digit([0-9])$/.exec(stroke.code);
  return physical === null ? null : Number(physical[1]);
}

/** Frets past 9 need a modifier: alt adds ten, alt and shift together add twenty. */
function fretFor(stroke: Keystroke, digit: number): number {
  if (!stroke.altKey) return digit;
  return stroke.shiftKey ? digit + 20 : digit + 10;
}

/**
 * What a keystroke means, or `null` when the board should let the browser have it.
 *
 * A modifier key means the stroke belongs to the browser or to undo, so nothing is claimed
 * from it: typing `c` writes a C, but `⌘c` is a copy and must pass straight through. Alt
 * and shift are ours, because they are how the arrows say how far to move.
 *
 * The current entry state is read for the two keys that toggle: a dot and a triplet are
 * either on or off, and a key that could only ever turn them on would need a second key to
 * turn them off again.
 *
 * `fretting` says the caret is on a tab staff. There a digit is a fret rather than a note
 * value -- which is the whole of how tablature is written -- so the note values move to the
 * same digits with shift held, and the palette offers them as well.
 */
export function entryFor(
  stroke: Keystroke,
  state: EntryState,
  fretting = false,
): EntryInput | null {
  if (stroke.ctrlKey || stroke.metaKey) return null;
  const key = stroke.key.toLowerCase();

  if (LETTERS.has(key)) {
    const step = key.toUpperCase() as "A" | "B" | "C" | "D" | "E" | "F" | "G";
    return stroke.shiftKey ? { kind: "chord", step } : { kind: "note", step };
  }

  const digit = digitOf(stroke);
  if (fretting && digit !== null && !(stroke.shiftKey && !stroke.altKey)) {
    return { kind: "fret", fret: fretFor(stroke, digit) };
  }

  const type = DURATIONS[key];
  if (type !== undefined) return { kind: "duration", type };

  switch (key) {
    case ".":
      return { kind: "dots", dots: state.duration.dots > 0 ? 0 : 1 };
    case "/":
      return {
        kind: "tuplet",
        ratio: state.duration.tuplet === null ? { actual: 3, normal: 2 } : null,
      };
    case "q":
      return shifted(state, 1);
    case "w":
      return shifted(state, -1);
    case "r":
      return { kind: "rest" };
    case "t":
      return { kind: "tie" };
    // S for the small note, which is what a grace note is called everywhere it is not
    // called a grace note. Shift makes it the one that is played on the beat.
    case "s":
      return {
        kind: "grace",
        grace: stroke.shiftKey
          ? state.grace === "appoggiatura"
            ? null
            : "appoggiatura"
          : state.grace === "acciaccatura"
            ? null
            : "acciaccatura",
      };
    case "backspace":
    case "delete":
      return { kind: "delete" };
    case "arrowleft":
      return { kind: "move", delta: -1 };
    case "arrowright":
      return { kind: "move", delta: 1 };
    case "arrowup":
      return raise(stroke, 1, fretting);
    case "arrowdown":
      return raise(stroke, -1, fretting);
    default:
      return null;
  }
}

/**
 * An up or down arrow, in whichever currency the modifiers ask for.
 *
 * Plain arrows move by a semitone, which is what writes the accidental a wrong note usually
 * needs. Alt moves by a letter instead, for the note that is on the wrong line rather than
 * the wrong side of a sharp, and shift moves by an octave -- seven letters, which keeps the
 * accidental exactly as it was.
 *
 * On a tab staff the plain arrows move to the next string instead, because a note there is
 * changed by typing another fret and what the hand needs the arrows for is the string it is
 * on. Up the page is toward string 1, which is the top line and the highest string.
 */
function raise(stroke: Keystroke, direction: number, fretting: boolean): EntryInput {
  if (stroke.shiftKey) return { kind: "pitch", by: "step", count: direction * 7 };
  if (stroke.altKey) return { kind: "pitch", by: "step", count: direction };
  if (fretting) return { kind: "string", delta: -direction };
  return { kind: "pitch", by: "semitone", count: direction };
}

/**
 * What a keystroke does when it is not writing a note.
 *
 * Kept apart from `entryFor` because these are the application's verbs rather than the
 * document's: a page decides what "save" or "play" mean, and a board that only wrote notes
 * would still want them. The page asks for one of these first, and only lets a stroke reach
 * note entry when there is none.
 */
export type ScoreShortcut =
  | "undo"
  | "redo"
  | "copy"
  | "cut"
  | "paste"
  | "selectAll"
  /** Type a chord symbol over the selection, which the host does by taking the caret. */
  | "chord"
  /** Type a stack of figures under the selection, the same way. */
  | "figures"
  /** Move what is selected a step in one direction, which is the last word on a page. */
  | "nudgeUp"
  | "nudgeDown"
  | "nudgeLeft"
  | "nudgeRight"
  | "open"
  | "save"
  | "exportXml"
  | "print"
  | "zoomIn"
  | "zoomOut"
  | "playPause"
  | "stop"
  | "loop"
  | "metronome"
  /** Start another line of music on the staff the caret is on. */
  | "addVoice"
  /** Take that line off the staff again, with everything hung on it. */
  | "removeVoice"
  /** Draw the selection on the staff above or below, the voice keeping it. */
  | "crossUp"
  | "crossDown"
  | "help";

const WITH_COMMAND: Readonly<Record<string, ScoreShortcut>> = {
  z: "undo",
  c: "copy",
  x: "cut",
  v: "paste",
  a: "selectAll",
  k: "chord",
  g: "figures",
  // The arrows already write music, so the nudge is where the arrows are with the command
  // key held -- which is also the one modifier note entry never claims.
  arrowup: "nudgeUp",
  arrowdown: "nudgeDown",
  arrowleft: "nudgeLeft",
  arrowright: "nudgeRight",
  o: "open",
  s: "save",
  e: "exportXml",
  p: "print",
  "=": "zoomIn",
  "+": "zoomIn",
  "-": "zoomOut",
};

/**
 * The command-key strokes that mean something else again with shift held.
 *
 * Read before {@link WITH_COMMAND}. Crossing a note to the staff above or below is where
 * the arrows go once the nudge has taken them with the command key, and it is where
 * Sibelius puts it too.
 */
const WITH_COMMAND_SHIFTED: Readonly<Record<string, ScoreShortcut>> = {
  arrowup: "crossUp",
  arrowdown: "crossDown",
};

const PLAIN: Readonly<Record<string, ScoreShortcut>> = {
  " ": "playPause",
  escape: "stop",
  l: "loop",
  m: "metronome",
  v: "addVoice",
  "?": "help",
};

/**
 * The few plain keys that mean something else with shift held.
 *
 * Read before {@link PLAIN}, and deliberately tiny: a key that does one thing and its
 * opposite is only worth the second reading where the two are the same errand. Adding a
 * voice and taking it away again are.
 */
const PLAIN_SHIFTED: Readonly<Record<string, ScoreShortcut>> = {
  v: "removeVoice",
};

/**
 * The application verb a keystroke asks for, or `null`.
 *
 * The command key is what separates the two halves: `c` writes a C and `⌘c` copies, and
 * nothing here claims a bare letter that note entry uses. `L`, `M` and `V` are free because
 * no note is spelled with them.
 */
export function actionFor(stroke: Keystroke): ScoreShortcut | null {
  const key = stroke.key.toLowerCase();
  if (stroke.ctrlKey || stroke.metaKey) {
    if (key === "z") return stroke.shiftKey ? "redo" : "undo";
    if (stroke.shiftKey) return WITH_COMMAND_SHIFTED[key] ?? WITH_COMMAND[key] ?? null;
    return WITH_COMMAND[key] ?? null;
  }
  if (stroke.altKey) return null;
  if (stroke.shiftKey) return PLAIN_SHIFTED[key] ?? PLAIN[key] ?? null;
  return PLAIN[key] ?? null;
}
